// Pure derivations behind the Coach Analytics Overview tab: the today strip and
// the chat-engagement panel. No React, no ambient clock.
//
// Every number here comes from fields the coach analytics payload already
// returns, EXCEPT the client count, which comes from the roster.
//
// WHY THE ROSTER: `totals.total_rooms` is NOT a client count. Its SQL
// (coach_analytics_service.py:601-613) counts DISTINCT room_id from
// RoomParticipants with no room_type filter, no participant_role filter,
// archived rooms included and no date range — "every room this person sits in
// right now, in any capacity". A coach who also works support tickets counts
// those rooms too. The authoritative assignment source is
// ClientSubscriptionCoaches, which is what GET /oversight/coaches/{id}/clients
// returns, so the page passes that roster length in.
//
// Likewise `totals.active_rooms` is ROOMS THE COACH MESSAGED IN, not distinct
// clients — same all-room-types caveat. It is labelled as rooms until the
// backend exposes a distinct-client count.
//
// Two metrics the design asks for — "clients who write back" and
// "conversations they opened" — are NOT derived: the first needs a count of
// distinct rooms a client replied in (only a message count exists), the second
// needs conversation-initiation attribution. Approximating either from
// `client_messages` would produce a plausible, wrong percentage, so the panel
// says they are missing instead.

/** Number of whole days a `YYYY-MM-DD` range covers, inclusive of both ends. */
export function rangeDays(payload) {
  const from = String(payload?.date_from ?? "").slice(0, 10);
  const to = String(payload?.date_to ?? "").slice(0, 10);
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) {
    // Fall back to the series length, which is zero-filled so every bucket in
    // the range is present.
    const buckets = Array.isArray(payload?.series) ? payload.series.length : 0;
    return buckets > 0 ? buckets : null;
  }
  return Math.round((b - a) / 86400000) + 1;
}

/** Finite number or null — the payload may send null, NaN or a string. */
export function num(value) {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** `a / b` guarded against a zero or unknown denominator. */
export function ratio(a, b) {
  const top = num(a);
  const bottom = num(b);
  if (top == null || bottom == null || bottom === 0) return null;
  return top / bottom;
}

/** Round to at most one decimal, dropping a trailing ".0". */
export function round1(value) {
  const n = num(value);
  if (n == null) return null;
  return Math.round(n * 10) / 10;
}

/**
 * The last bucket in the series — "today" when the range ends today and the
 * payload is partial. Returns null for an empty series.
 *
 * @param {Array<Object>|null|undefined} series
 * @returns {Object|null}
 */
export function latestBucket(series) {
  const rows = Array.isArray(series) ? series : [];
  return rows.length ? rows[rows.length - 1] : null;
}

/**
 * The live band above the Overview tab.
 *
 * `partial` is threaded through because "messages today" is only genuinely
 * today's number when the range includes the current UTC day; otherwise the
 * last bucket is the last day of a historical range and must not be labelled
 * "today".
 *
 * @param {Object|null|undefined} payload - The coach analytics response.
 * @returns {{ isOnlineNow: boolean, isToday: boolean, onlineSeconds: number|null,
 *   messages: number|null, roomsMessaged: number|null, totalRooms: number|null,
 *   medianFirstResponseSeconds: number|null, sessions: number|null }}
 */
export function todayStats(payload) {
  const totals = payload?.totals ?? null;
  const bucket = latestBucket(payload?.series);
  const isToday = payload?.partial === true;

  return {
    isOnlineNow: totals?.is_online_now === true,
    isToday,
    // `online_seconds_today` is live and range-independent, so it is right even
    // when the selected range ended in the past — but only meaningful as
    // "today" when the range actually includes today.
    onlineSeconds: isToday ? num(totals?.online_seconds_today) : num(bucket?.online_seconds),
    messages: num(bucket?.messages_sent),
    // Rooms, not clients — see the note at the top of this file.
    roomsMessaged: num(bucket?.active_rooms),
    totalRooms: num(totals?.total_rooms),
    medianFirstResponseSeconds: num(bucket?.median_first_response_seconds),
    sessions: num(bucket?.sessions_count),
  };
}

/** Metric ids on the chat-engagement panel, so tests and JSX agree on one spelling. */
export const CHAT_METRIC_IDS = {
  CLIENTS_ASSIGNED: "clientsAssigned",
  ROOMS_MESSAGED: "roomsMessaged",
  MESSAGES_SENT: "messagesSent",
  CLIENT_REPLIES: "clientReplies",
  REPLY_SPEED: "replySpeed",
  MESSAGES_PER_CLIENT_WEEK: "messagesPerClientWeek",
  SLA: "sla",
};

/**
 * The chat-engagement metrics, each with the plain-English sentence that says
 * what it means.
 *
 * `value` is left null when the input is unknown; callers render a dash rather
 * than a zero, because "no data" and "zero messages" are different answers.
 *
 * @param {Object|null|undefined} payload - The coach analytics response.
 * @param {Object} [options]
 * @param {number|null} [options.assignedClients] - Roster length from
 *   `GET /oversight/coaches/{id}/clients`. The ONLY authoritative client count;
 *   see the note at the top of this file for why `total_rooms` is not one.
 * @returns {Array<{id: string, label: string, value: number|null, kind: string,
 *   denominator: number|null, tone: string, sentence: string|null}>}
 */
export function chatEngagement(payload, { assignedClients = null } = {}) {
  const totals = payload?.totals ?? null;
  const days = rangeDays(payload);
  const weeks = days ? days / 7 : null;

  const roomsMessaged = num(totals?.active_rooms);
  const roomsTotal = num(totals?.total_rooms);
  const clients = num(assignedClients);
  const sent = num(totals?.messages_sent);
  const replies = num(totals?.client_messages);
  const median = num(totals?.median_first_response_seconds);
  const p90 = num(totals?.p90_first_response_seconds);
  const slaHit = num(totals?.sla_hit_count) ?? 0;
  const slaMissed = num(totals?.sla_missed_count) ?? 0;
  const slaTotal = slaHit + slaMissed;

  const perDay = days ? round1(ratio(sent, days)) : null;
  const perRoom = round1(ratio(sent, roomsMessaged));
  const perClientWeek = weeks ? round1(ratio(sent, clients != null ? clients * weeks : null)) : null;
  const slaRate = slaTotal > 0 ? slaHit / slaTotal : null;

  return [
    {
      id: CHAT_METRIC_IDS.CLIENTS_ASSIGNED,
      label: "Clients assigned",
      value: clients,
      denominator: null,
      kind: "count",
      tone: "neutral",
      // Point-in-time: ClientSubscriptionCoaches carries no assignment window,
      // so this is "right now", never "during this period".
      sentence: clients == null ? null : "On their roster right now, not just this period.",
    },
    {
      id: CHAT_METRIC_IDS.ROOMS_MESSAGED,
      label: "Rooms they messaged in",
      value: roomsMessaged,
      denominator: roomsTotal,
      kind: "fraction",
      tone: "neutral",
      // Deliberately "rooms", not "clients": this counts every room type the
      // coach is in, including support rooms they work as an agent. The
      // distinct-client version needs a Messages⨝Rooms join the API does not
      // expose yet.
      sentence:
        roomsMessaged == null
          ? null
          : "Rooms of every type, including support — not a client count.",
    },
    {
      id: CHAT_METRIC_IDS.MESSAGES_SENT,
      label: "Messages sent",
      value: sent,
      denominator: null,
      kind: "count",
      tone: "neutral",
      sentence:
        perDay == null
          ? null
          : `About ${perDay} a day${perRoom != null ? `, ${perRoom} per room` : ""}.`,
    },
    {
      id: CHAT_METRIC_IDS.CLIENT_REPLIES,
      label: "Client messages in their rooms",
      value: replies,
      denominator: null,
      kind: "count",
      tone: "neutral",
      // Not a per-coach reply count: the rollup counts every client message
      // that day in any room the coach also messaged in, so in a room with two
      // coaches BOTH get the full number.
      sentence:
        replies == null
          ? null
          : "Counted per room, so a room with two coaches counts for both.",
    },
    {
      id: CHAT_METRIC_IDS.REPLY_SPEED,
      label: "Usual reply speed",
      value: median,
      denominator: null,
      kind: "seconds",
      tone: "neutral",
      sentence:
        median == null
          ? null
          : `Half their replies are faster than this${
              p90 != null ? ". 9 in 10 land inside the p90 below" : ""
            }.`,
    },
    {
      id: CHAT_METRIC_IDS.MESSAGES_PER_CLIENT_WEEK,
      label: "Messages per client each week",
      value: perClientWeek,
      denominator: null,
      kind: "decimal",
      tone: "neutral",
      sentence:
        perClientWeek == null
          ? null
          : "Averaged across the whole roster, including clients they never messaged.",
    },
    {
      id: CHAT_METRIC_IDS.SLA,
      label: "Answered in time",
      value: slaRate,
      denominator: null,
      kind: "percent",
      tone:
        slaRate == null ? "neutral" : slaRate >= 0.9 ? "success" : slaRate < 0.7 ? "danger" : "neutral",
      sentence:
        slaTotal === 0 ? null : `${slaHit} hit the response target, ${slaMissed} missed it.`,
    },
  ];
}

/** Buckets for "how recently each room was active", worst last. */
export const CADENCE_BUCKETS = [
  { id: "today", label: "Today or yesterday", maxDays: 1, tone: "success" },
  { id: "d2to3", label: "2–3 days", maxDays: 3, tone: "success" },
  { id: "d4to7", label: "4–7 days", maxDays: 7, tone: "warn" },
  { id: "d8to14", label: "8–14 days", maxDays: 14, tone: "warn" },
  { id: "d15plus", label: "15 days or more", maxDays: Infinity, tone: "danger" },
  { id: "never", label: "Never messaged", maxDays: null, tone: "danger" },
];

/**
 * Distribution of the roster across the cadence buckets, plus the longest-silent
 * clients for the "going quiet" list.
 *
 * NOTE ON MEANING: this reads `last_activity_at`, which is the last message
 * either way — not the last message FROM the coach. The panel is labelled to
 * match. "How recently each client heard from THEM" needs a
 * `last_coach_message_at` field the endpoint does not return yet.
 *
 * @param {Array<Object>} clients - `/oversight/coaches/{id}/clients` rows.
 * @param {Object} [options]
 * @param {Date} [options.now]
 * @param {number} [options.quietCount=3] - How many quiet clients to name.
 * @param {(id: string) => number|null} [options.daysSince] - Injected for testing.
 * @returns {{ buckets: Array<Object>, total: number, quiet: Array<Object>, quietTotal: number }}
 */
export function contactCadence(clients, options = {}) {
  const list = Array.isArray(clients) ? clients : [];
  const now = options.now || new Date();
  const quietCount = options.quietCount ?? 3;
  const daysSince = options.daysSince;

  const counts = CADENCE_BUCKETS.reduce((acc, b) => {
    acc[b.id] = 0;
    return acc;
  }, {});
  const aged = [];

  list.forEach((c) => {
    const never = !c?.last_activity_at || (c?.message_count ?? 0) === 0;
    if (never) {
      counts.never += 1;
      aged.push({ client: c, days: null });
      return;
    }
    const days = daysSince ? daysSince(c) : defaultDaysSince(c.last_activity_at, now);
    if (days == null) {
      counts.never += 1;
      aged.push({ client: c, days: null });
      return;
    }
    const bucket = CADENCE_BUCKETS.find((b) => b.maxDays != null && days <= b.maxDays);
    counts[(bucket ?? CADENCE_BUCKETS[4]).id] += 1;
    aged.push({ client: c, days });
  });

  // Never-messaged first (they have no age but are the most urgent), then the
  // longest silence. Matches the "most neglected" sort on the Clients tab.
  const quiet = aged
    .filter((a) => a.days == null || a.days > CADENCE_BUCKETS[2].maxDays)
    .sort((a, b) => (b.days ?? Infinity) - (a.days ?? Infinity))
    .slice(0, quietCount);

  return {
    buckets: CADENCE_BUCKETS.map((b) => ({ ...b, count: counts[b.id] })),
    total: list.length,
    quiet,
    quietTotal: aged.filter((a) => a.days == null || a.days > CADENCE_BUCKETS[2].maxDays).length,
  };
}

/** Whole UTC days since an ISO timestamp. Local to this module; the Clients tab has its own. */
function defaultDaysSince(iso, now) {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const then = new Date(ms);
  return Math.round(
    (Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) -
      Date.UTC(then.getUTCFullYear(), then.getUTCMonth(), then.getUTCDate())) /
      86400000
  );
}
